import { useMemo, useState } from "react";

import { t } from "../../../i18n";
import { cn } from "../../../lib/cn";
import { trpc } from "../../../lib/trpc";
import { Button, Card, EmptyState, Icon, Input, SkeletonGate, Stack } from "../../ui";
import { SettlementCard } from "../components/SettlementCard";
import { SettlementYearGroup } from "../components/SettlementYearGroup";
import { CreateSettlementDialog } from "../dialogs/CreateSettlementDialog";
import { groupSettlementsByYear, type SettlementListItem } from "../utils";

type SettlementStatus = SettlementListItem["status"];

interface SettlementsTabProps {
  householdId: string;
  onSelect: (id: string) => void;
}

export function SettlementsTab({ householdId, onSelect }: SettlementsTabProps) {
  const settlements = trpc.settlement.list.useQuery({ groupId: householdId });
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<Set<SettlementStatus>>(() => new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);

  const toggleStatus = (s: SettlementStatus) =>
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const data = settlements.data;

  const filteredData = useMemo(() => {
    if (!data) return undefined;
    const q = search.trim().toLocaleLowerCase("nb-NO");
    return data.filter((s) => {
      if (statusFilter.size > 0 && !statusFilter.has(s.status)) return false;
      if (q && !s.title.toLocaleLowerCase("nb-NO").includes(q)) return false;
      return true;
    });
  }, [data, search, statusFilter]);

  const years =
    filteredData && filteredData.length > 0 ? groupSettlementsByYear(filteredData) : null;
  const isFiltered = search.trim().length > 0 || statusFilter.size > 0;
  const showSearch = (data?.length ?? 0) > 1;

  const statusOptions: Array<{ value: SettlementStatus; label: string; count: number }> = [
    {
      value: "open",
      label: t.settlementStatus.open,
      count: data?.filter((s) => s.status === "open").length ?? 0,
    },
    {
      value: "awaiting_approval",
      label: t.settlementStatus.awaitingApproval,
      count: data?.filter((s) => s.status === "awaiting_approval").length ?? 0,
    },
    {
      value: "locked",
      label: t.settlementStatus.locked,
      count: data?.filter((s) => s.status === "locked").length ?? 0,
    },
  ];

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-medium text-ink-700 text-sm">
          {t.household.settlements.countHeading(data?.length ?? 0)}
        </h2>
        <Button variant="primary" iconLeft="plus" onClick={() => setCreateOpen(true)}>
          {t.household.settlements.newSettlement}
        </Button>
      </div>

      {showSearch && (
        <>
          <div className="mb-3 flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <Input
                iconLeft="search"
                placeholder={t.household.settlements.searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={t.household.settlements.searchLabel}
                className="h-11 sm:h-9"
              />
            </div>
            <Button
              variant="secondary"
              iconLeft="filter"
              aria-label={t.household.settlements.filtersButton}
              aria-expanded={filtersOpen}
              aria-controls="settlement-filters-row"
              onClick={() => setFiltersOpen((v) => !v)}
              className={cn(
                "h-11 w-11 shrink-0 px-0 sm:h-9 sm:w-auto sm:px-3",
                filtersOpen &&
                  "border-ink-400 bg-ink-100 text-ink-900 shadow-none hover:border-ink-400 hover:bg-ink-100 dark:border-ink-400 dark:bg-ink-200",
              )}
            >
              <span className="hidden sm:inline">{t.household.settlements.filtersButton}</span>
              {statusFilter.size > 0 && (
                <span className="ml-1 inline-flex h-4 min-w-[1.25rem] items-center justify-center rounded bg-primary px-1 text-[10px] text-primary-fg">
                  {statusFilter.size}
                </span>
              )}
              <Icon
                name={filtersOpen ? "chevron-up" : "chevron-down"}
                size="xs"
                className="hidden sm:inline-flex"
              />
            </Button>
          </div>

          {filtersOpen && (
            <div id="settlement-filters-row" className="mb-3 flex flex-wrap items-center gap-1.5">
              {statusOptions.map((opt) => {
                const active = statusFilter.has(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleStatus(opt.value)}
                    className={cn(
                      "inline-flex h-10 items-center gap-1.5 rounded-md px-3 font-medium text-xs transition-colors sm:h-7 sm:px-2.5",
                      active
                        ? "bg-primary/10 text-primary hover:bg-primary/15 dark:bg-primary/20 dark:hover:bg-primary/25"
                        : "bg-ink-100 text-ink-700 hover:bg-ink-200 dark:bg-ink-200 dark:hover:bg-ink-300",
                    )}
                  >
                    {opt.label}
                    <span
                      className={cn(
                        "inline-flex h-4 min-w-[1.25rem] items-center justify-center rounded px-1 text-[10px]",
                        active ? "bg-primary text-primary-fg" : "bg-ink-200 text-ink-600",
                      )}
                    >
                      {opt.count}
                    </span>
                  </button>
                );
              })}
              {statusFilter.size > 0 && (
                <button
                  type="button"
                  onClick={() => setStatusFilter(new Set())}
                  className="ml-1 text-accent-700 text-xs hover:text-accent-900 dark:text-accent-300 dark:hover:text-accent-200"
                >
                  {t.household.settlements.filtersClear}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {settlements.isPending && (
        <SkeletonGate loading={true} shape="list" rows={4} label={t.common.loading}>
          {null}
        </SkeletonGate>
      )}

      {data?.length === 0 && (
        <Card padding="none">
          <EmptyState
            icon="receipt"
            title={t.household.settlements.emptyTitle}
            description={t.household.settlements.emptyDescription}
            action={
              <Button variant="primary" iconLeft="plus" onClick={() => setCreateOpen(true)}>
                {t.household.settlements.emptyAction}
              </Button>
            }
          />
        </Card>
      )}

      {isFiltered && filteredData?.length === 0 && (data?.length ?? 0) > 0 && (
        <Card padding="lg">
          <p className="text-center text-ink-500 text-sm">{t.household.settlements.searchEmpty}</p>
        </Card>
      )}

      {years && years.length === 1 && years[0] && (
        <Stack gap={2}>
          {years[0].settlements.map((s) => (
            <SettlementCard
              key={s.id}
              settlement={s}
              onClick={() => onSelect(s.id)}
              search={search}
            />
          ))}
        </Stack>
      )}

      {years && years.length > 1 && (
        <div className="flex flex-col gap-5">
          {years.map((group, idx) => (
            <SettlementYearGroup
              key={group.year}
              group={group}
              defaultOpen={idx < 2}
              onSelect={onSelect}
              search={search}
            />
          ))}
        </div>
      )}

      <CreateSettlementDialog
        householdId={householdId}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          onSelect(id);
        }}
      />
    </>
  );
}
